import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { notifySuccess, notifyError } from "../../utils/ToastifyProvider";
import { useNavigate } from "react-router";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getToken = () => localStorage.token;

export const useChats = ({ token, limit = 10, search = "" }) => {
  return useInfiniteQuery({
    queryKey: ["chats", limit, search],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await axios.get(
        `${BASE_URL}/chat/all-chats?page=${pageParam}&limit=${limit}&search=${encodeURIComponent(
          search
        )}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );

      return {
        chats: (res.data?.chats || []).filter((chat) =>
          Array.isArray(chat.users)
        ),
        nextPage: res.data?.nextPage ?? null,
        hasMore: res.data?.hasMore ?? false,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        return (allPages.length || 1) + 1;
      }
      return undefined;
    },
    enabled: !!token,
    retry: 1,
    staleTime: 30000, 
    cacheTime: 300000,
    refetchOnWindowFocus: false, 
    refetchOnMount: false,
  });
};

export const useChatMessages = (chatId, options = {}) => {
  return useInfiniteQuery({
    queryKey: ["chatMessages", chatId],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await axios.get(
        `${BASE_URL}/messages/chat/${chatId}?page=${pageParam}&limit=50`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      return res.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.pagination?.hasMore) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    enabled: !!chatId,
    staleTime: 10000,
    ...options,
  });
};

export const useSendChatRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId) => {
      const response = await axios.post(
        `${BASE_URL}/chat/request`,
        { userId },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      notifySuccess("Chat request sent successfully!");
      queryClient.invalidateQueries({ queryKey: ["chats"] });
      queryClient.invalidateQueries({ queryKey: ["pendingChats"] });
    },
    onError: (error) => {
      notifyError(error.response?.data?.message || "Failed to send chat request");
    },
  });
};

export const sendChatRequest = async (userId) => {
  const response = await axios.post(
    `${BASE_URL}/chat/request`,
    { userId },
    {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    }
  );
  return response.data;
};

export const usePendingChats = () => {
  return useQuery({
    queryKey: ["pendingChats"],
    queryFn: async () => {
      const res = await axios.get(`${BASE_URL}/chat/pending-request`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return res.data;
    },
    onError: (err) =>
      notifyError(err.message || "Failed to load pending chats"),
  });
};

export const useApproveChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chatId) =>
      axios.put(
        `${BASE_URL}/chat/${chatId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      ),

    onSuccess: () => {
      notifySuccess("Chat approved!");
      queryClient.invalidateQueries({ queryKey: ["pendingChats"] });
      queryClient.invalidateQueries({ queryKey: ["chats"] });
    },

    onError: (err) => {
      notifyError(err.message || "Failed to approve chat");
    },
  });
};

// ✅ COMPLETELY OPTIMIZED: No invalidation at all
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageData) => {
      const isFormData = messageData instanceof FormData;
      let formData;

      if (isFormData) {
        formData = messageData;
      } else {
        formData = new FormData();
        formData.append("chatId", messageData.chatId);

        if (messageData.content)
          formData.append("content", messageData.content);
        if (messageData.replyTo)
          formData.append("replyTo", messageData.replyTo);

        if (messageData.files) {
          const fileArray = Array.isArray(messageData.files)
            ? messageData.files
            : [messageData.files];
          
          fileArray.forEach((file) => {
            formData.append("files", file);
          });
          
          const audioFile = fileArray.find(f => f.type.startsWith('audio/'));
          if (audioFile && audioFile.duration !== undefined && audioFile.duration > 0) {
            formData.append("duration", audioFile.duration.toString());
          }
        }
      }

      const res = await axios.post(
        `${BASE_URL}/messages/send-message`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return res.data;
    },
    onSuccess: (newMessage) => {
      const chatId = newMessage.chat || newMessage.chatId;

      // ✅ 1. Update messages cache
      queryClient.setQueryData(["chatMessages", chatId], (oldData) => {
        if (!oldData) {
          return { pages: [{ messages: [newMessage] }], pageParams: [1] };
        }

        const { pages, pageParams } = oldData;
        const allMessages = pages.flatMap((p) => p.messages || []);

        if (allMessages.find((m) => m._id === newMessage._id)) {
          return oldData;
        }

        const newPages = [...pages];
        const lastPage = newPages[newPages.length - 1];

        newPages[newPages.length - 1] = {
          ...lastPage,
          messages: [...(lastPage.messages || []), newMessage],
        };

        return { pages: newPages, pageParams };
      });

      // ✅ 2. Update ALL chat query keys (handles different search params)
      const chatQueries = queryClient.getQueriesData({ queryKey: ["chats"] });
      
      chatQueries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        queryClient.setQueryData(queryKey, (old) => {
          if (!old) return old;

          const updatedPages = old.pages.map((page, pageIndex) => {
            // Only update the first page (most recent chats)
            if (pageIndex !== 0) return page;

            const chatExists = page.chats.some(chat => chat._id === chatId);
            
            if (!chatExists) {
              // Chat not in this query, skip
              return page;
            }

            const updatedChats = page.chats.map((chat) => {
              if (chat._id === chatId) {
                return {
                  ...chat,
                  latestMessage: {
                    _id: newMessage._id,
                    content: newMessage.content || "",
                    createdAt: newMessage.createdAt || new Date().toISOString(),
                    sender: newMessage.sender,
                    files: newMessage.files || [],
                    fileType: newMessage.fileType || "text",
                    messageType: newMessage.messageType || "text",
                  },
                  updatedAt: newMessage.createdAt || new Date().toISOString(),
                };
              }
              return chat;
            });

            // Sort chats by updatedAt (most recent first)
            const sortedChats = [...updatedChats].sort(
              (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
            );

            return {
              ...page,
              chats: sortedChats,
            };
          });

          return {
            ...old,
            pages: updatedPages,
          };
        });
      });

      // ✅ 3. NO INVALIDATION - Socket events will handle updates from server
      console.log("✅ Message sent, cache updated optimistically");
    },
    onError: (err) => {
      notifyError(err.response?.data?.message || "Failed to send message");
    },
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chatId }) => {
      const res = await axios.post(
        `${BASE_URL}/messages/${chatId}/mark-as-read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      return res.data;
    },
    onSuccess: (_, { chatId }) => {
      // ✅ Update cache directly, no invalidation
      const chatQueries = queryClient.getQueriesData({ queryKey: ["chats"] });
      
      chatQueries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        queryClient.setQueryData(queryKey, (old) => {
          if (!old) return old;

          const updatedPages = old.pages.map((page) => ({
            ...page,
            chats: page.chats.map((chat) => {
              if (chat._id === chatId) {
                return {
                  ...chat,
                  unreadCount: 0,
                };
              }
              return chat;
            }),
          }));

          return {
            ...old,
            pages: updatedPages,
          };
        });
      });
    },
    onError: (err) => {
      console.error("Failed to mark as read", err);
    },
  });
};

export const useCreateGroupChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, users }) => {
      const res = await axios.post(
        `${BASE_URL}/chat/group`,
        { name, users },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      return res.data;
    },
    onSuccess: (data) => {
      notifySuccess(`✅ Group "${data.chatName}" created`);
      queryClient.invalidateQueries(["chats"]);
    },
    onError: (err) => {
      notifyError(err.response?.data?.message || "Failed to create group chat");
    },
  });
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (msgId) => {
      const res = await axios.delete(
        `${BASE_URL}/messages/delete-msg/${msgId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      return res.data;
    },
    onSuccess: (_, msgId) => {
      notifySuccess("Message deleted 🗑");
      // Only invalidate messages, not chats
      queryClient.invalidateQueries(["chatMessages"]);
    },
    onError: (err) => {
      notifyError(err.response?.data?.message || "Failed to delete message");
    },
  });
};

export const useGroupMembers = (chatId) => {
  return useQuery({
    queryKey: ["groupMembers", chatId],
    queryFn: async () => {
      if (!chatId) return null;
      const res = await axios.get(`${BASE_URL}/chat/group-info/${chatId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return res.data;
    },
    enabled: !!chatId,
    staleTime: 1000 * 60,
  });
};

export const useRenameGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chatId, name }) => {
      const res = await axios.put(
        `${BASE_URL}/chat/group-rename/${chatId}`,
        { name },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["groupMembers", variables.chatId]);
      queryClient.invalidateQueries(["chats"]);
    },
    onError: (error) => {
      console.error("Failed to rename group:", error);
    },
  });
};

export const useDeleteGroup = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (chatId) => {
      const res = await axios.delete(
        `${BASE_URL}/chat/${chatId}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      return res.data;
    },
    onSuccess: (data, chatId) => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries(["chats"]);
      queryClient.invalidateQueries(["groupMembers", chatId]);
      
      // Remove the deleted chat from cache
      queryClient.removeQueries(["groupMembers", chatId]);
      
      // Optional: Navigate to chat list
      navigate('/chats');
      
      // Show success message (optional - you can use toast)
      console.log("Group deleted successfully:", data);
    },
    onError: (error) => {
      console.error("Failed to delete group:", error);
      // Optional: Show error toast
    },
  });
};

export const useAddGroupMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chatId, userId }) => {
      const res = await axios.put(
        `${BASE_URL}/chat/group-add/${chatId}`,
        { userId },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["groupMembers", variables.chatId]);
      queryClient.invalidateQueries(["chats"]);
    },
    onError: (error) => {
      console.error("Failed to add member:", error);
    },
  });
};

export const useRemoveGroupMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chatId, userId }) => {
      const res = await axios.put(
        `${BASE_URL}/chat/group-remove/${chatId}`,
        { userId },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["groupMembers", variables.chatId]);
      queryClient.invalidateQueries(["chats"]);
    },
    onError: (error) => {
      console.error("Failed to remove member:", error);
    },
  });
};

export const useUserChats = (userId) => {
  return useQuery({
    queryKey: ["userChats", userId],
    queryFn: async () => {
      try {
        const res = await axios.get(`${BASE_URL}/chat/user-chats/${userId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        return Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        if (err.response?.status === 404) {
          return [];
        }
        throw err;
      }
    },
    enabled: !!userId,
    staleTime: 1000 * 60,
    retry: false,
  });
};

export const useApplyLeave = () => {
  return useMutation({
    mutationFn: async (date) => {
      if (!date) throw new Error("Date is required");

      const response = await axios.post(
        `${BASE_URL}/task/apply-leave`,
        { date },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );

      return response.data;
    },
    onError: (error) => {
      notifyError("Error applying leave:", error);
    },
    onSuccess: (data) => {
      notifySuccess("Leave applied successfully:");
    },
  });
};

export const useEditMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ messageId, content }) => {
      const res = await axios.put(
        `${BASE_URL}/messages/${messageId}/edit`,
        { content },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables) => {
      const updatedMessage = data.message;
      const chatId = updatedMessage.chat || updatedMessage.chatId;

      // ✅ 1. Update messages cache
      queryClient.setQueryData(["chatMessages", chatId], (oldData) => {
        if (!oldData) return oldData;

        const { pages, pageParams } = oldData;
        
        const newPages = pages.map((page) => ({
          ...page,
          messages: page.messages.map((msg) => {
            if (msg._id === variables.messageId) {
              return {
                ...msg,
                content: updatedMessage.content,
                edited: true,
                editedAt: updatedMessage.editedAt,
                editHistory: updatedMessage.editHistory,
              };
            }
            return msg;
          }),
        }));

        return { pages: newPages, pageParams };
      });

      // ✅ 2. Update chat list if this is the latest message
      const chatQueries = queryClient.getQueriesData({ queryKey: ["chats"] });
      
      chatQueries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        queryClient.setQueryData(queryKey, (old) => {
          if (!old) return old;

          const updatedPages = old.pages.map((page) => ({
            ...page,
            chats: page.chats.map((chat) => {
              if (
                chat._id === chatId &&
                chat.latestMessage?._id === variables.messageId
              ) {
                return {
                  ...chat,
                  latestMessage: {
                    ...chat.latestMessage,
                    content: updatedMessage.content,
                    edited: true,
                  },
                };
              }
              return chat;
            }),
          }));

          return {
            ...old,
            pages: updatedPages,
          };
        });
      });

      notifySuccess("Message edited successfully");
      console.log("✅ Message edited, cache updated");
    },
    onError: (err) => {
      const errorMessage = err.response?.data?.message || "Failed to edit message";
      notifyError(errorMessage);
      console.error("Edit message error:", err);
    },
  });
};

// ✅ NEW: Make a user a group admin
export const useMakeGroupAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chatId, userId }) => {
      const res = await axios.put(
        `${BASE_URL}/chat/group/make-admin/${chatId}`,
        { userId },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables) => {
      notifySuccess("User promoted to group admin");
      queryClient.invalidateQueries(["groupMembers", variables.chatId]);
      queryClient.invalidateQueries(["chats"]);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || "Failed to make user admin";
      notifyError(errorMessage);
      console.error("Make admin error:", error);
    },
  });
};

// ✅ NEW: Remove group admin privileges
export const useRemoveGroupAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chatId, userId }) => {
      const res = await axios.put(
        `${BASE_URL}/chat/group/remove-admin/${chatId}`,
        { userId },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables) => {
      notifySuccess("Admin privileges removed");
      queryClient.invalidateQueries(["groupMembers", variables.chatId]);
      queryClient.invalidateQueries(["chats"]);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || "Failed to remove admin privileges";
      notifyError(errorMessage);
      console.error("Remove admin error:", error);
    },
  });
};